import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { catchError, finalize } from 'rxjs/operators';
import { SpinnerService } from '../spinner/spinner.service';
import { SalesforceObject } from '../../models/salesforce/salesforce-object.model';
import { PicklistValue, SalesforceField } from '../../models/salesforce/salesforce-field.model';
import { delayedRetry } from '../eloqua/utils';

@Injectable({
    providedIn: 'root',
})
export class SalesforceObjectService {
    readonly #http = inject(HttpClient);
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #spinnerService = inject(SpinnerService);

    private URL: string = environment.publicApiHost + '/salesforce';

    listSafesforceObjects(searchString: string, salesforceOauth: number): Observable<SalesforceObject[]> {
        const spinnerKey = 'SalesforceObjectService.listSalesforceObjects()';
        this.#spinnerService.addProcess(spinnerKey, 'Searching for Salesforce objects...');

        const httpOptions = {
            params: new HttpParams().set('oauthId', salesforceOauth.toString()),
        };
        let url = this.URL + '/objects';
        if (searchString) {
            url = url + `?search=${searchString}`;
        }

        return this.#http.get<SalesforceObject[]>(url, httpOptions).pipe(
            delayedRetry(1000, 3),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving Salesforce Objects from Relationship One. Please try again later.'
                );
                return of<SalesforceObject[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerKey))
        );
    }

    getObjectFields(objectName: string, salesforceOauth: number): Observable<SalesforceField[]> {
        const spinnerKey = 'SalesforceObjectService.getObjectFields()';
        this.#spinnerService.addProcess(spinnerKey, 'Searching for Salesforce object fields...');

        const httpOptions = {
            params: new HttpParams().set('oauthId', salesforceOauth.toString()),
        };

        return this.#http.get<SalesforceField[]>(this.URL + `/objects/${objectName}/fields`, httpOptions).pipe(
            delayedRetry(1000, 3),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving Salesforce object fields from Relationship One. Please try again later.'
                );
                return of<SalesforceField[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess(spinnerKey))
        );
    }

    getPicklistValues(objectName: string, fieldName: string, salesforceOauth: number): Observable<PicklistValue[]> {
        const spinnerKey = 'SalesforceObjectService.getPicklistValues()';
        this.#spinnerService.addProcess(spinnerKey, 'Searching for Salesforce picklist values...');

        const httpOptions = {
            params: new HttpParams().set('oauthId', salesforceOauth.toString()),
        };

        return this.#http
            .get<PicklistValue[]>(this.URL + `/objects/${objectName}/fields/${fieldName}`, httpOptions)
            .pipe(
                delayedRetry(1000, 3),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while retrieving Salesforce picklist values from Relationship One. Please try again later.'
                    );
                    return of<PicklistValue[]>();
                }),
                finalize(() => this.#spinnerService.completeProcess(spinnerKey))
            );
    }
}
